import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Property, getImageUrl } from '../api/api';
import { useLanguage } from '../contexts/LanguageContext';

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const { t } = useLanguage();
  const [imgError, setImgError] = useState(false);

  const primaryImage = property.images?.find(img => img.is_primary) || property.images?.[0];
  const imageUrl = primaryImage ? getImageUrl(primaryImage.image_url) : '';
  const typeLabel = property.property_type === 'apartment' ? t('apartment') : t('house');

  // Debug logging
  console.log('Property:', property.id, 'Images:', property.images);
  console.log('Primary Image:', primaryImage);
  console.log('Image URL:', imageUrl);

  return (
    <Link to={`/property/${property.id}`} className="block group">
      <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group-hover:-translate-y-1 flex flex-col h-full">
        {/* Image Container */}
        <div className="aspect-[4/3] bg-gradient-to-br from-primary-100 to-primary-200 relative overflow-hidden flex-shrink-0">
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={property.title}
              onError={() => setImgError(true)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-primary-400 bg-gray-100">
              <span className="text-4xl">🏠</span>
            </div>
          )}

          {/* Type badge */}
          <div className="absolute top-2.5 left-2.5">
            <span className="bg-white/95 backdrop-blur-sm text-primary-700 text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
              {typeLabel}
            </span>
          </div>

          {/* Status badge */}
          {property.status === 'sold' && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white text-lg font-extrabold tracking-wider px-3 py-1 bg-red-600 rounded-lg shadow">
                {t('sold')}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3.5 sm:p-4 flex flex-col flex-1 justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-1 truncate group-hover:text-primary-600 transition-colors">
              {property.title}
            </h3>

            <div className="flex items-center text-gray-500 text-xs sm:text-sm mb-2.5">
              <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="truncate">{property.city}{property.address ? `, ${property.address}` : ''}</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <div>
                <p className="text-base sm:text-lg font-extrabold text-primary-600 leading-tight">
                  {Number(property.price).toLocaleString()} <span className="text-xs font-semibold text-gray-500">{t('sum')}</span>
                </p>
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-500 font-medium">
                {property.area && (
                  <span>{property.area} {t('sqm')}</span>
                )}
                {property.rooms && (
                  <span>• {property.rooms} {t('roomsShort')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
